import { useState } from "react";
import { API, MODELS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Upload, ScanSearch, Loader2, ShieldAlert, CheckCircle2, ListChecks, Users,
  Trophy, FileCheck2, Coins, Calendar, MapPin, Info, Lightbulb, AlertTriangle, Link2, Printer
} from "lucide-react";

const CATEGORIES = [
  { key: "eligibility", label: "Eligibility", icon: Users, color: "sky" },
  { key: "scope", label: "Scope", icon: ScanSearch, color: "primary" },
  { key: "thematic_priorities", label: "Thematic priorities", icon: Trophy, color: "amber" },
  { key: "evaluation_criteria", label: "Evaluation criteria", icon: ListChecks, color: "violet" },
  { key: "required_documents", label: "Required documents", icon: FileCheck2, color: "emerald" },
  { key: "application_steps", label: "Application steps", icon: ListChecks, color: "primary" },
  { key: "budget_rules", label: "Budget rules", icon: Coins, color: "amber" },
  { key: "restrictions", label: "Restrictions", icon: ShieldAlert, color: "red" },
  { key: "fit_tips", label: "Fit tips", icon: Lightbulb, color: "emerald" },
  { key: "risk_flags", label: "Risk flags", icon: AlertTriangle, color: "red" },
];
const COLORS = {
  primary: "border-primary/40 bg-primary/5 text-foreground",
  sky:    "border-sky-300 bg-sky-50 text-sky-950",
  amber:  "border-amber-300 bg-amber-50 text-amber-950",
  violet: "border-violet-300 bg-violet-50 text-violet-950",
  emerald:"border-emerald-300 bg-emerald-50 text-emerald-950",
  red:    "border-red-300 bg-red-50 text-red-900",
};
const ICON_COLORS = { primary:"text-primary", sky:"text-sky-700", amber:"text-amber-700", violet:"text-violet-700", emerald:"text-emerald-700", red:"text-red-700" };

export default function NotificationAnalyzer() {
  const [source, setSource] = useState("file");
  const [file, setFile] = useState(null);
  const [url, setUrl] = useState("");
  const [modelIdx, setModelIdx] = useState(0);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const analyze = async () => {
    setLoading(true); setResult(null);
    try {
      const m = MODELS[modelIdx];
      let res;
      if (source === "file") {
        if (!file) { setLoading(false); return toast.error("Select a PDF, DOCX or TXT file"); }
        const fd = new FormData();
        fd.append("file", file);
        fd.append("model_provider", m.provider);
        fd.append("model_name", m.name);
        res = await fetch(`${API}/funding/analyze`, {
          method: "POST",
          headers: { Authorization: `Bearer ${localStorage.getItem("gf_token")}` },
          body: fd,
        });
      } else {
        if (!url.trim()) { setLoading(false); return toast.error("Paste a URL first"); }
        res = await fetch(`${API}/funding/analyze-url`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("gf_token")}` },
          body: JSON.stringify({ url: url.trim(), model_provider: m.provider, model_name: m.name }),
        });
      }
      if (!res.ok) { const err = await res.json().catch(()=>({})); throw new Error(err.detail || "Analysis failed"); }
      setResult(await res.json());
      toast.success("Notification analysed");
    } catch (e) { toast.error(e.message || "Analysis failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-6xl mx-auto p-8">
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <div className="overline">Awareness</div>
          <h1 className="font-display text-5xl mt-2">Notification analyser</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">Upload a call-for-proposals file or paste the agency's URL. GrantForge extracts eligibility, scope, evaluation criteria, budget rules, restrictions, and expert fit tips — so you know precisely how to shape a winning proposal.</p>
        </div>
        {result && <Button variant="outline" className="rounded-full" onClick={()=>window.print()} data-testid="analyzer-print"><Printer className="w-4 h-4 mr-2"/>Print report</Button>}
      </div>

      <div className="card-crisp rounded-lg p-6 mb-6">
        <Tabs value={source} onValueChange={setSource}>
          <TabsList>
            <TabsTrigger value="file" data-testid="analyzer-src-file"><Upload className="w-4 h-4 mr-1"/>Upload file</TabsTrigger>
            <TabsTrigger value="url" data-testid="analyzer-src-url"><Link2 className="w-4 h-4 mr-1"/>Paste URL</TabsTrigger>
          </TabsList>
          <TabsContent value="file">
            <div className="grid md:grid-cols-3 gap-4 items-end mt-3">
              <div className="md:col-span-2">
                <Label>Notification file</Label>
                <label className="mt-2 flex items-center gap-3 border border-dashed border-border rounded-md px-4 py-6 cursor-pointer hover:bg-secondary/50" data-testid="analyzer-file-drop">
                  <Upload className="w-5 h-5 text-primary"/>
                  <div>
                    <div className="text-sm">{file ? file.name : "Click to choose a PDF, DOCX, or TXT"}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">Max 8 MB</div>
                  </div>
                  <input type="file" accept=".pdf,.docx,.txt,.md" className="hidden" onChange={(e)=>setFile(e.target.files?.[0] || null)} data-testid="analyzer-file-input"/>
                </label>
              </div>
              <ModelAndRun modelIdx={modelIdx} setModelIdx={setModelIdx} onRun={analyze} loading={loading}/>
            </div>
          </TabsContent>
          <TabsContent value="url">
            <div className="grid md:grid-cols-3 gap-4 items-end mt-3">
              <div className="md:col-span-2">
                <Label>Notification URL</Label>
                <Input placeholder="https://agency.example.org/call-for-proposals-2026" value={url} onChange={(e)=>setUrl(e.target.value)} data-testid="analyzer-url-input"/>
                <div className="text-xs text-muted-foreground mt-1">The page will be fetched, the call text extracted, then analysed.</div>
              </div>
              <ModelAndRun modelIdx={modelIdx} setModelIdx={setModelIdx} onRun={analyze} loading={loading}/>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {result && (
        <motion.div initial={{opacity:0, y:10}} animate={{opacity:1, y:0}} transition={{duration:0.3}} className="space-y-5" data-testid="analyzer-result">
          <div className="card-crisp rounded-lg p-6">
            <div className="overline">{result.agency || "Agency"}</div>
            <h2 className="font-display text-4xl mt-1 leading-tight">{result.title || "—"}</h2>
            <div className="mt-4 grid md:grid-cols-4 gap-3 text-sm">
              <Stat icon={MapPin} label="Geography" value={result.geography}/>
              <Stat icon={Calendar} label="Deadline" value={result.deadline || result.submission_deadline_iso}/>
              <Stat icon={Coins} label="Funding" value={result.funding_amount}/>
              <Stat icon={Info} label="Duration" value={result.project_duration}/>
            </div>
          </div>

          {Array.isArray(result.key_dates) && result.key_dates.length > 0 && (
            <div className="card-crisp rounded-lg p-5">
              <div className="font-display text-2xl mb-3 flex items-center gap-2"><Calendar className="w-5 h-5 text-primary"/>Key dates</div>
              <ol className="relative border-l-2 border-primary/30 ml-2 space-y-3">
                {result.key_dates.map((d, i) => (
                  <li key={i} className="ml-4">
                    <span className="absolute -left-1.5 w-3 h-3 rounded-full bg-primary"></span>
                    <div className="text-sm"><b>{d.date}</b> · {d.label}</div>
                  </li>
                ))}
              </ol>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            {CATEGORIES.map(({ key, label, icon: Icon, color }) => {
              const list = result[key];
              if (!Array.isArray(list) || list.length === 0) return null;
              return (
                <div key={key} className={`rounded-lg p-5 border ${COLORS[color]}`} data-testid={`analyzer-cat-${key}`}>
                  <div className="flex items-center gap-2 mb-3"><Icon className={`w-5 h-5 ${ICON_COLORS[color]}`}/><div className="font-display text-xl">{label}</div></div>
                  <ul className="space-y-1.5 text-sm">
                    {list.map((it, j) => (
                      <li key={j} className="flex items-start gap-2"><CheckCircle2 className={`w-3.5 h-3.5 mt-1 ${ICON_COLORS[color]}`}/><span>{it}</span></li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>

          {(result.contact || result.website) && (
            <div className="card-crisp rounded-lg p-5">
              <div className="font-display text-2xl mb-2">Contact</div>
              {result.contact && <div className="text-sm">{result.contact}</div>}
              {result.website && <a href={result.website} target="_blank" rel="noreferrer" className="text-primary hover:underline text-sm break-all">{result.website}</a>}
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
}

function ModelAndRun({ modelIdx, setModelIdx, onRun, loading }) {
  return (
    <div>
      <Label>Model</Label>
      <Select value={String(modelIdx)} onValueChange={(v)=>setModelIdx(Number(v))}>
        <SelectTrigger data-testid="analyzer-model"><SelectValue/></SelectTrigger>
        <SelectContent>{MODELS.map((m,i)=><SelectItem key={i} value={String(i)}>{m.label}</SelectItem>)}</SelectContent>
      </Select>
      <Button className="w-full rounded-full mt-3" onClick={onRun} disabled={loading} data-testid="analyzer-run">
        {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Analysing…</> : <><ScanSearch className="w-4 h-4 mr-2"/>Analyse notification</>}
      </Button>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-md border border-border p-3">
      <div className="flex items-center gap-1.5 overline"><Icon className="w-3 h-3"/>{label}</div>
      <div className="mt-1 font-medium">{value || "—"}</div>
    </div>
  );
}
